
import React, { useState, useEffect } from "react";
import * as openpgp from "openpgp";
import { motion } from "framer-motion";
import "./PGPEncryptor.css";
import { fixArmoredMessage, fixArmoredPublicKey } from "../utils/pgpHelpers";


const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const PgpEncryptor = ({ onSendViaMessaging }) => {
  const [publicKeyArmored, setPublicKeyArmored] = useState("");
  const [plaintextMessage, setPlaintextMessage] = useState("");
  const [encryptedMessage, setEncryptedMessage] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [connected, setConnected] = useState(false);

  const apiUrl =
    process.env.REACT_APP_API_URL || "https://api.online-encryption.ovh";

  useEffect(() => {
    fetch(`${apiUrl}/is_connected`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) {
          setConnected(true);
        }
      })
      .catch((err) => console.error("IsConnected check failed", err));
  }, [apiUrl]);

  const handleDownload = () => {
    const blob = new Blob([encryptedMessage], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "encrypted_message.asc";

    link.click();
    URL.revokeObjectURL(url);
  };

  // Asynchronous function to handle the encryption process.
  const handleEncrypt = async () => {
    try {
      // Normalize and parse the armored public key.
      const sanitizedKey = fixArmoredPublicKey(publicKeyArmored);
      const publicKey = await openpgp.readKey({ armoredKey: sanitizedKey });

      // Create a message object from the plaintext.
      const message = await openpgp.createMessage({ text: plaintextMessage });

      // Encrypt the message using the public key.
      const encrypted = await openpgp.encrypt({
        message,
        encryptionKeys: publicKey,
      });

      setEncryptedMessage(fixArmoredMessage(encrypted));

      setError("");

      setCopied(false);
    } catch (err) {
      setError("Encryption failed: " + err.message);
      setEncryptedMessage("");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(encryptedMessage).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <motion.div
      className="pgp-container card"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <h1>PGP Encryption</h1>
      <div className="form-group">
        <label htmlFor="publicKey">Public Key</label>
        <textarea
          id="publicKey"
          value={publicKeyArmored}
          onChange={(e) => setPublicKeyArmored(e.target.value)}
          rows="10"
          placeholder="Paste your armored public key here..."
        />
      </div>
      <div className="form-group">
        <label htmlFor="plaintextMessage">Plaintext Message</label>
        <textarea
          id="plaintextMessage"
          value={plaintextMessage}
          onChange={(e) => setPlaintextMessage(e.target.value)}
          rows="10"
          placeholder="Enter the message you want to encrypt..."
        />
      </div>
      <button className="btn" onClick={handleEncrypt}>
        Encrypt
      </button>
      {error && <p className="feedback error">{error}</p>}
      {encryptedMessage && (
        <div className="result">
          <h2>Encrypted Message</h2>
          <textarea
            className="message-box"
            readOnly
            value={encryptedMessage}
            rows={10}
          />
          <button className="btn" onClick={handleCopy}>
            Copy to Clipboard
          </button>
          <button className="btn" onClick={handleDownload}>
            Download
          </button>
          {connected && onSendViaMessaging && (
            <button
              className="btn"
              onClick={() => onSendViaMessaging(encryptedMessage)}
            >
              Send via Messaging
            </button>
          )}

          {copied && <p className="feedback">Copied!</p>}
        </div>
      )}
    </motion.div>
  );
};

export default PgpEncryptor;
